import { numbersFromZeroToMaxTuple } from "./numbersFromZeroToMax.js";
import { isObject, isPropertyKey } from "./primitive-types.js";
import { convertKebabToCamelCase } from "./strings.js";
import type { ArrayElement, AsMutableArray, MutableOrImmutableArray, ToMutableOrImmutableArray } from "./array-checks.js";
import type { ExpandSimpleObject } from "./expand-type.js";
import type { IfAnyTypeExtends, IfSubtype, IfTypeEquals } from "./TypeEqualityUtils.js";
import type { NumbersFromZeroToMaxTuple } from "./numbersFromZeroToMax.js";
import type { Nil } from "./primitive-types.js";
import type { KebabToCamelCase } from "./strings.js";
import type { IfNever } from "./conditional.js";
export type TryGetValue<T, Prop extends PropertyKey, TDefault = never> = Prop extends keyof T ? T[ Prop ] : TDefault;

export type TryGetNestedValue<T, Path extends readonly PropertyKey[], TDefault = never> =
  Path extends readonly []
  ? T
  :
  Path extends readonly [ infer K, ...infer Remaining extends readonly PropertyKey[] ]
  ? (K extends keyof T ? TryGetNestedValue<T[ K ], Remaining, TDefault> : TDefault)
  : never;

export type GetOptionalKeys<T> = keyof {
  [ K in keyof T as Omit<T, K> extends T ? K : never ]: T[ K ];
};
export type ValuePossiblyEmpty<T> = IfAnyTypeExtends<[ null, undefined, "" ], T>;
export type ValueNeverEmpty<T> =
  [ ValuePossiblyEmpty<T> ] extends [ false ] ? true : false;

export type ValuesOf<T> = T[ keyof T ];

export type GetPossibleEmptyProps<T extends object, IncludeOptionalProps extends boolean = true> =
  Extract<(
    | ValuesOf<{ [ K in keyof T as ([ ValuePossiblyEmpty<T[ K ]> ] extends [ true ] ? K : never) ]: K }>
    | ([ IncludeOptionalProps ] extends [ true ] ? GetOptionalKeys<T> : never)
  ), keyof T>;


type _GetNonEmptyKeysWithoutCheckingOptionalKeys<T extends object>
  = ValuesOf<{ [ K in keyof T as ([ ValuePossiblyEmpty<T[ K ]> ] extends [ false ] ? K : never) ]: K }>;
type _GetNonEmptyKeys<T extends object, ExcludeOptionalProps extends boolean = true, NonEmptyKeys = _GetNonEmptyKeysWithoutCheckingOptionalKeys<T>> =
  [ ExcludeOptionalProps ] extends [ true ]
  ? Exclude<NonEmptyKeys, GetOptionalKeys<T>>
  : NonEmptyKeys;
export type GetNonEmptyKeys<T extends object, ExcludeOptionalProps extends boolean = true> =
  Extract<_GetNonEmptyKeys<T, ExcludeOptionalProps>, keyof T>;


type IRequiredAndOptionalOptions<R extends object = {}, O extends object = {}, TAdditional extends object = {}> =
  ExpandSimpleObject<{
    required?: R,
    optional?: O,
  } & TAdditional>;

export type IObjectFromPartsOptions<R extends object = {}, O extends object = {}, P extends object = {}> =
  IRequiredAndOptionalOptions<R, O, { preserve?: P }>;

export type DefaultObjectFromPartsOptions = IObjectFromPartsOptions<object, object, object>;

export type KeyValuePair<K extends PropertyKey, V> = { key: K, value: V };

type AnyKeyValuePair = KeyValuePair<PropertyKey, unknown>;

export type IRecordFromPartsOptions<R extends AnyKeyValuePair, O extends AnyKeyValuePair, TAdditional extends object = {}> =
  IRequiredAndOptionalOptions<R, O, { additionalProps?: TAdditional }>;

export type DefaultRecordFromPartsOptions = IRecordFromPartsOptions<AnyKeyValuePair, AnyKeyValuePair, object>;


type _RecordFromParts<
  ReqKey extends PropertyKey,
  ReqValue,
  OptKey extends PropertyKey,
  OptValue,
  TAdditional extends object = {}
> =
  ExpandSimpleObject<
    Record<ReqKey, ReqValue> &
    Partial<Record<OptKey, OptValue>> &
    TAdditional
  >;

type _RecordFromPartsWithObject<
  S extends DefaultRecordFromPartsOptions
> =
  S extends IRecordFromPartsOptions<KeyValuePair<infer ReqKey, infer ReqValue>, KeyValuePair<infer OptKey, infer OptValue>, infer TAdditional extends object>
  ? _RecordFromParts<ReqKey, ReqValue, OptKey, OptValue, TAdditional>
  : never;

export type RecordFromParts<S extends DefaultRecordFromPartsOptions> =
  _RecordFromPartsWithObject<S>;

type _ObjectFromParts<
  TRequired extends object,
  TOptional extends object,
  TPreserve extends object,
  TAll = { [ K in keyof TRequired ]-?: TRequired[ K ] } & { [ K in keyof TOptional ]?: TOptional[ K ] } & TPreserve
> = { [ K in keyof TAll ]: TAll[ K ] };

export type ObjectFromParts<S extends DefaultObjectFromPartsOptions> =
  S extends IObjectFromPartsOptions<infer R, infer O, infer P>
  ? _ObjectFromParts<R, O, P>
  : never;
export type UnknownValueTypeRecord<TRequired extends PropertyKey, TOptional extends PropertyKey, TAdditional extends object = {}> =
  RecordFromParts<{ required: { key: TRequired, value: unknown }, optional: { key: TOptional, value: unknown }, additionalProps: TAdditional }>;

type _ITrueFalseOptions<
  T,
  F,
  AdditionalOptions extends DefaultObjectFromPartsOptions = {},
> =
  ExpandSimpleObject<
    { T: T, F: F } &
    ObjectFromParts<AdditionalOptions>
  >;

export type ITrueFalseOptions<
  T,
  F,
  AdditionalOptions extends { optional?: object, required?: object } = {}
> = _ITrueFalseOptions<T, F, AdditionalOptions>;

export type TrueFalseOptions<T, F> = ITrueFalseOptions<T, F>;

type IAnyTrueFalseOptions<TRequired extends PropertyKey = never, TOptional extends PropertyKey = never, TAdditional extends object = {}> = UnknownValueTypeRecord<"T" | "F" | TRequired, TOptional, TAdditional>;

export type AnyTrueFalseOptions = IAnyTrueFalseOptions<keyof TrueFalseOptions<unknown, unknown>>;
export type DefaultTrueFalseOptions = TrueFalseOptions<true, false>;

export type InvertTrueFalseOptions<O extends AnyTrueFalseOptions> = {
  T: O[ "F" ],
  F: O[ "T" ]
};

type _IBooleanIntersectionOptions<T, F, Both, Neither, AllowAny extends boolean> =
  ITrueFalseOptions<T, F, { optional: { Both: Both, Neither: Neither, AllowAny: AllowAny } }>;

type IAnyBooleanIntersectionOptions<TRequired extends PropertyKey = never, TOptional extends PropertyKey = never, TAdditional extends object = {}> =
  IAnyTrueFalseOptions<TRequired, "Both" | "Neither" | TOptional, TAdditional>;

export type IBooleanIntersectionOptions<T, F, Both, Neither, AllowAny extends boolean> =
  _IBooleanIntersectionOptions<T, F, Both, Neither, AllowAny>;

type AnyBooleanIntersectionOptions = IAnyBooleanIntersectionOptions<never, "Both" | "Neither", { AllowAny?: boolean }>;

/** If the the type T is the `any` type, then return type Y else return type N; */
export type IfAny<U, O extends AnyTrueFalseOptions = DefaultTrueFalseOptions> =
  0 extends 1 & U
  ? O[ "T" ]
  : O[ "F" ];

export type IfAnyOrUnknown<U, O extends AnyTrueFalseOptions = DefaultTrueFalseOptions> =
  unknown extends U
  ? O[ "T" ]
  : O[ "F" ];
export type IfUnknown<U, O extends AnyTrueFalseOptions = DefaultTrueFalseOptions> =
  IfAnyOrUnknown<U, {
    T: IfAny<U, InvertTrueFalseOptions<O>>,
    F: O[ "F" ]
  }>;

type _IfAnyUnknownOrNever<
  U,
  O extends { T: unknown, F: unknown } | { Any: unknown, Unknown: unknown, Never: unknown, F: unknown } = { T: true, F: false },
  S extends { Any: unknown, Unknown: unknown, Never: unknown, F: unknown } = O extends { T: infer T, F: infer F } ? { Any: T, Unknown: T, Never: T, F: F } : O
> =
  IfAny<U, {
    T: S[ "Any" ],
    F: IfUnknown<U, {
      T: S[ "Unknown" ],
      F: IfNever<U, {
        T: S[ "Never" ],
        F: S[ "F" ]
      }>
    }>
  }>;
export type IfAnyUnknownOrNever<U, O extends { T: unknown, F: unknown } | { Any: unknown, Unknown: unknown, Never: unknown, F: unknown } = { T: true, F: false }> =
  _IfAnyUnknownOrNever<U, O>;


type _IfTrueOrFalse<
  U,
  O extends AnyBooleanIntersectionOptions = DefaultTrueFalseOptions,
  T = O extends { T: infer TT } ? TT : never,
  F = O extends { F: infer FF } ? FF : never
> =
  [ IfAnyOrUnknown<U> ] extends [ true ]
  ? ([ TryGetValue<O, "AllowAny", false> ] extends [ true ] ? T : TryGetValue<O, "Neither", false>)
  :
  [ U ] extends [ boolean ]
  ?
  [ boolean ] extends [ U ]
  ? TryGetValue<O, "Both", false>
  :
  [ true ] extends [ U ]
  ? T
  :
  [ false ] extends [ U ]
  ? F
  : never
  : TryGetValue<O, "Neither", false>;

export type IfTrueOrFalse<U, O extends AnyBooleanIntersectionOptions = DefaultTrueFalseOptions>
  = _IfTrueOrFalse<U, O>


type _TupleContains<A extends readonly any[], U> =
  A extends readonly []
  ? false
  :
  A extends readonly [ infer First, ...infer Remaining extends readonly any[] ]
  ?
  [ IfTypeEquals<First, U> ] extends [ true ]
  ? true
  :
  [ IfAnyUnknownOrNever<U> ] extends [ true ]
  ? _TupleContains<Remaining, U>
  :
  [ IfAnyUnknownOrNever<First> ] extends [ true ]
  ? _TupleContains<Remaining, U>
  :
  [ U, First ] extends [ First, U ]
  ? true
  : _TupleContains<Remaining, U>
  : false;
export type TupleContains<A extends readonly any[], T> = _TupleContains<A, T>;


export type ApplyAND<A extends readonly [ ...readonly boolean[] ], O extends { CastBooleanToTrue: boolean } = { CastBooleanToTrue: false }> =
  A extends readonly []
  ? false
  :
  [ TupleContains<A, false> ] extends [ true ]
  ? false
  :
  [ TupleContains<A, boolean> ] extends [ true ]
  ?
  [ O[ "CastBooleanToTrue" ] ] extends [ true ]
  ? true
  : false
  :
  [ A[ number ] ] extends [ true ]
  ? true
  : false;


export type ApplyOR<A extends readonly [ ...readonly boolean[] ], O extends { CastBooleanToTrue: boolean } = { CastBooleanToTrue: false }> =
  A extends readonly []
  ? false
  :
  [ TupleContains<A, true> ] extends [ true ]
  ? true
  :
  [ TupleContains<A, boolean>, O[ "CastBooleanToTrue" ] ] extends [ true, true ]
  ? true
  : false;

// type TypeExtends<U, V> = [ U ] extends [ V ] ? true : false;

// type LooselyExtends<U, V> =
//   [ IfTypeEquals<U, V> ] extends [ true ]
//   ? true
//   :
//   [ IfAnyUnknownOrNever<U>, IfAnyUnknownOrNever<V> ] extends [ true, boolean ] | [ boolean, true ]
//   ? false
//   :
//   [ U ] extends [ V ]
//   ? true
//   :
//   [ IfTypeExtends<U, DefinedPrimitive>, IfTypeExtends<V, DefinedPrimitive> ] extends [ true, true ]
//   ?
//   IfTypeExtends<U, UnboxPrimitiveOrLiteral<V>>
//   : never;


// type LooselyEqual<U, V> =
//   [ IfTypeEquals<U, V> ] extends [ true ]
//   ? true
//   :
//   [ IfAnyUnknownOrNever<U> ] extends [ true ]
//   ? false
//   :
//   [ IfAnyUnknownOrNever<V> ] extends [ true ]
//   ? false
//   :
//   [ U ] extends [ V ]
//   ?
//   [ ExtractExcessPropertiesObject<V, U> ] extends [ infer E ]
//   ? (
//     [ V & E ] extends [ U ]
//     ? true
//     : { res: "never: [ V & E ] extends [ U ]", U: U, V: V, E: E }
//   )
//   : "never: ExtractExcessPropertiesObject<V, U>"
//   :
//   [ V ] extends [ U ]
//   ?
//   [ ExtractExcessPropertiesObject<U, V> ] extends [ infer E ]
//   ? (
//     [ U & E ] extends [ V ]
//     ? true
//     : { res: "never: [ U & E ] extends [ V ]", U: U, V: V, E: E }
//   )
//   : "never: ExtractExcessPropertiesObject<U, V>"
//   : false;

export type AND<U1 extends boolean, U2 extends boolean, O extends { CastBooleanToTrue: boolean } = { CastBooleanToTrue: false }> =
  ApplyAND<[ U1, U2 ], O>;

export type OR<T1 extends boolean, T2 extends boolean, O extends { CastBooleanToTrue: boolean } = { CastBooleanToTrue: false }> =
  ApplyOR<[ T1, T2 ], O>;
export type Not<T extends boolean> =
  T extends true
  ? false
  :
  T extends false
  ? true
  : never;



export type IfStringLiteral<U, O extends { T: unknown, F: unknown, Neither?: unknown } = { T: true, F: false }> = IfSubtype<U, string, O>;

// type InferBaseType<TBase, TDerived> =
//   [ TDerived, TBase ] extends [ TBase, TDerived ]
//   ? "TBASE equals TDerived"
//   :
//   [ TBase ] extends [ infer P extends DefinedPrimitive ]
//   ? (
//     TDerived extends infer TFirstPart extends TBase & infer Extra
//     ? { extra: Extra, "Has extra" }
//     : never
//   )
//   : "TBase is not a primitive!";

export type IfNumberLiteral<U, O extends { T: unknown, F: unknown, Neither?: unknown } = { T: true, F: false }> = IfSubtype<U, number, O>;
export type IfBooleanLiteral<U, O extends { T: unknown, F: unknown, Neither?: unknown } = { T: true, F: false }> = IfSubtype<U, boolean, O>;

export type IfLiteralType<U, T = true, F = false> =
  IfStringLiteral<U, {
    T: T,
    F: IfNumberLiteral<U, {
      T: T,
      F: IfBooleanLiteral<U, {
        T: T,
        F: F
      }>
    }>
  }>;

export type ExtractPropertiesOfType<TObject extends object, TType> = {
  [ K in keyof TObject as TObject[ K ] extends TType ? K : never ]: TObject[ K ];
}

export type KeyOfType<TObject extends object, TType> = keyof ExtractPropertiesOfType<TObject, TType>;

export type UnionToIntersection<U> =
  (U extends any ? (k: U) => void : never) extends ((k: infer I) => void)
  ? I
  : never;

export type ExtractLiteralNumber<T> = T extends number
  ? (
    number extends T
    ? never
    : T
  )
  : (
    T extends `${ infer N extends number }`
    ? ExtractLiteralNumber<N>
    : never
  );

export type GetTupleIndicesTuple<T> =
  T extends { length: number }
  ? NumbersFromZeroToMaxTuple<T[ "length" ]>
  : never;


export type ReverseObjectMapOfArrayIndices<T extends MutableOrImmutableArray<any>> = {
  [ I in keyof T as (T[ I ] extends PropertyKey ? T[ ExtractLiteralNumber<I> ] : never) ]: ExtractLiteralNumber<I>
};



export type WithDefault<T, TDefault> = T extends any ? (T extends Nil ? TDefault : T) : never;
export type DeepMutable<T> = {
  -readonly [ P in keyof T ]: T[ P ] extends ReadonlyArray<infer U> ? DeepMutable<U>[] : DeepMutable<T[ P ]>;
};

export type ShallowMutable<T> = T extends ReadonlyArray<infer U> ? U[] : {
  -readonly [ P in keyof T ]: T[ P ];
};

type ArrayToIntersection<TArray extends MutableOrImmutableArray<any>, TResult extends any = {}> =
  AsMutableArray<TArray> extends [ infer T, ...infer Remaining ]
  ? ArrayToIntersection<Remaining, TResult & T>
  : TResult;

export type ExpandShallow<T> =
  T extends object
  ? { [ K in keyof T ]: T[ K ] } & {}
  : T;
export type ExpandDeep<T> =
  T extends object
  ? { [ K in keyof T ]: ExpandDeep<T[ K ]> } & {}
  : T;


export type ApplyMergeObjects<A extends MutableOrImmutableArray<any>> = ExpandShallow<ArrayToIntersection<A>>;

export type MergeObjects<
  T1,
  T2,
  T3 = {},
  T4 = {},
  T5 = {},
  T6 = {},
  R extends MutableOrImmutableArray<any> = []
> = ApplyMergeObjects<[ T1, T2, T3, T4, T5, T6, ...R ]>;



export type ReverseObjectMap<T extends Record<keyof T, PropertyKey>> = {
  [ V in T[ keyof T ] ]: {
    [ K in keyof T ]: T[ K ] extends V ? K : never;
  }[ keyof T ];
}



export type MaybeImplements<TMaybe, TFallback = {}> = TMaybe | TFallback;


// #region FromEntriesTyped
/** Type: Remove all readonly annotations on an object recursively */
type DeepWriteable<T> = { -readonly [ P in keyof T ]: DeepWriteable<T[ P ]> };

/** Type: Return type `X` unchanged if it extends `Y`, otherwise force type `X` to be type `Y` */
type Cast<X, Y> = X extends Y ? X : Y

/** Type: Create a type from an array of key/value pairs */
type FromEntriesHelper<T> = T extends [ infer Key, any ][]
  ? { [ K in Cast<Key, PropertyKey> ]: Extract<ArrayElement<T>, [ K, any ]>[ 1 ] }
  : { [ key in string ]: any }

/** Type: Create a type from an array of key/value pairs. 
 *  Note: Removes `readonly` annotations on all properties */
export type FromEntriesTyped<T extends Iterable<ToMutableOrImmutableArray<[ PropertyKey, unknown ]>>> = FromEntriesHelper<DeepWriteable<T>>
// #endregion FromEntriesTyped


export type EnumFromKeys<Keys extends string, CamelCaseProps extends boolean = true> =
  CamelCaseProps extends true
  ? { [ key in Keys as KebabToCamelCase<key> ]: key }
  : { [ key in Keys ]: key };






export type KeysOfValue<T, TCondition> = { [ K in keyof T ]: T[ K ] extends TCondition ? K : never; }[ keyof T ];
export function fromEntriesTyped<const T extends Iterable<ToMutableOrImmutableArray<[ PropertyKey, unknown ]>>> (entries: T): FromEntriesTyped<T>;

export function fromEntriesTyped(entries) {
  return Object.fromEntries(entries);
}


export function getTupleIndicesTuple<const T extends ToMutableOrImmutableArray<[ ...any[] ]>> (value: T): GetTupleIndicesTuple<T> {
  if (value && typeof value === "object" && "length" in value && typeof value.length === "number") {
    return numbersFromZeroToMaxTuple(value.length);
  }
  return [] as GetTupleIndicesTuple<T>;
}

export function reverseObjectMapOfArrayIndices<const T extends ToMutableOrImmutableArray<[ ...any[] ]>> (value: T): ReverseObjectMapOfArrayIndices<T> {
  const tupleIndices = getTupleIndicesTuple(value);
  const reversedKeyObj = (tupleIndices as any[]).reduce((result, idx) => {
    const valAtIdx = value[idx];
    if (!isPropertyKey(valAtIdx)) {
      throw new Error(`Value at index is not a valid property key: ${valAtIdx ?? ''}`);
    }
    result[valAtIdx] = idx;
    return result;
  }, {}) as ReverseObjectMapOfArrayIndices<T>;
  return reversedKeyObj;
}
export function createReverseObjectMap<T extends Record<keyof T, PropertyKey>> (value: T): { [ K in keyof ReverseObjectMap<T> ]: ReverseObjectMap<T>[ K ] } {
  if (isObject(value)) {
    const keys = Object.keys(value);
    return Object.fromEntries(
      keys.map(k => {
        const val = value[k];
        return [val, k];
      })
    );
  }
  throw new Error("Parameter 'value' is not an object");
}

export function exhaustiveGuard (value: never, message = "ERROR! Reached forbidden guard function with unexpected value"): never {
  throw new Error(`${message}: ${JSON.stringify(value)}`);
}

export function createEnumFromKeys<const Keys extends readonly string[], TCamelCase extends boolean> (
  keys: Keys,
  camelCaseProps?: TCamelCase
): EnumFromKeys<ArrayElement<Keys>, [TCamelCase] extends [false] ? false : true> {
  camelCaseProps ??= true as TCamelCase;
  return keys.reduce((result, key) => {
    const k = (camelCaseProps ? convertKebabToCamelCase(key) : key);
    result[k] = key;
    return result;
  }, {}) as EnumFromKeys<ArrayElement<Keys>, [ TCamelCase ] extends [ false ] ? false : true>;
}

export function getTypedObjectKeys<T extends object> (obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}